import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, AlertTriangle, XCircle, CheckCircle, RefreshCw,
  Loader2, ChevronDown, ChevronRight, Clock, Rocket, Image, Eye, X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusIcon = (status) => {
  if (status === 'pass') return <CheckCircle size={14} className="text-emerald-600 flex-shrink-0" />;
  if (status === 'warn') return <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />;
  return <XCircle size={14} className="text-red-600 flex-shrink-0" />;
};

const statusBg = (status) => {
  if (status === 'pass') return 'bg-emerald-50 border-emerald-200';
  if (status === 'warn') return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
};

const SectionCard = ({ sectionKey, section, onRerun, running }) => {
  const passed = section.checks.filter(c => c.status === 'pass').length;
  const warns = section.checks.filter(c => c.status === 'warn').length;
  const fails = section.checks.filter(c => c.status === 'fail').length;
  const total = section.checks.length;
  const allPass = fails === 0 && warns === 0;
  // v6.32.51 — auto-expand sections that aren't fully passing so the
  // operator never has to click into a section just to see what's red
  // (the original UX hid every check behind a collapsed accordion).
  const [expanded, setExpanded] = useState(!allPass);

  return (
    <div className={`border-2 ${fails > 0 ? 'border-red-300' : warns > 0 ? 'border-amber-300' : 'border-emerald-300'} bg-white`} data-testid={`audit-section-${sectionKey}`}>
      <div className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left gap-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-3 flex-1 text-left min-w-0"
          data-testid={`audit-section-toggle-${sectionKey}`}
        >
          {allPass ? (
            <div className="w-8 h-8 bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <CheckCircle size={18} />
            </div>
          ) : fails > 0 ? (
            <div className="w-8 h-8 bg-red-100 text-red-600 flex items-center justify-center">
              <XCircle size={18} />
            </div>
          ) : (
            <div className="w-8 h-8 bg-amber-100 text-amber-600 flex items-center justify-center">
              <AlertTriangle size={18} />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-bold text-sm truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{section.title}</div>
            <div className="text-xs text-gray-500 truncate">{section.description}</div>
          </div>
        </button>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-emerald-700 font-semibold">{passed} pass</span>
            {warns > 0 && <span className="text-amber-600 font-semibold">{warns} warn</span>}
            {fails > 0 && <span className="text-red-600 font-semibold">{fails} fail</span>}
            <span className="text-gray-400">/ {total}</span>
          </div>
          {/* v6.32.51 — inline Re-run pill on any non-passing section. The
              audit endpoint only re-runs the WHOLE audit, but at least
              the operator no longer has to scroll to the top. */}
          {!allPass && onRerun && (
            <button
              onClick={(e) => { e.stopPropagation(); onRerun(); }}
              disabled={running}
              className="px-2 py-1 text-[11px] font-semibold border border-gray-300 hover:bg-gray-100 rounded disabled:opacity-50 inline-flex items-center gap-1"
              data-testid={`audit-section-rerun-${sectionKey}`}
              title="Re-run the full audit (this is the same as the top button — handy when you've scrolled down to see a failure)"
            >
              {running ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              Re-run
            </button>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-0.5"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-gray-200 p-3 space-y-1 max-h-[400px] overflow-y-auto">
          {section.checks.map((check, i) => (
            <div key={i} className={`flex items-start gap-2 px-3 py-2 text-xs border ${statusBg(check.status)}`}>
              {statusIcon(check.status)}
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-gray-800">{check.check}</span>
                <span className="text-gray-500 ml-2">{check.detail}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ImageGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState(null);
  const [brokenUrls, setBrokenUrls] = useState(new Set());

  const fetchImages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audit/image-gallery`);
      setImages(res.data.images || []);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { fetchImages(); }, []);

  const handleImgError = (url) => {
    setBrokenUrls(prev => new Set([...prev, url]));
  };

  const gradeOrder = ['K', '1', '2', '3-4', '5-6', '7-8', '9-12'];
  const sortGrades = (grades) => [...grades].sort((a, b) => gradeOrder.indexOf(a) - gradeOrder.indexOf(b));

  const broken = images.filter(img => brokenUrls.has(img.image_url));
  const working = images.filter(img => !brokenUrls.has(img.image_url));

  return (
    <div className="border-2 border-[#002FA7]/20 bg-white" data-testid="image-gallery-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
        data-testid="image-gallery-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
            <Image size={18} />
          </div>
          <div>
            <div className="font-bold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Big Picture Image Gallery
            </div>
            <div className="text-xs text-gray-500">
              Visual spot-check all {images.length} unique cultural scene images
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {images.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-emerald-700 font-semibold">{working.length} loaded</span>
              {broken.length > 0 && <span className="text-red-600 font-semibold">{broken.length} broken</span>}
            </div>
          )}
          {expanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 p-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={20} className="animate-spin text-gray-400 mr-2" />
              <span className="text-sm text-gray-500">Loading images...</span>
            </div>
          ) : (
            <>
              {broken.length > 0 && (
                <div className="mb-4 border-2 border-red-200 bg-red-50 p-3 flex items-start gap-2">
                  <XCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-red-800">
                    <strong>{broken.length} broken image(s):</strong>{' '}
                    {broken.map(b => b.context).join(', ')}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {images.map((img, i) => (
                  <div
                    key={i}
                    className={`group border-2 ${brokenUrls.has(img.image_url) ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-[#002FA7]/40'} transition-all cursor-pointer`}
                    onClick={() => !brokenUrls.has(img.image_url) && setPreview(img)}
                    data-testid={`gallery-image-${i}`}
                  >
                    <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                      {brokenUrls.has(img.image_url) ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <XCircle size={24} className="text-red-400" />
                        </div>
                      ) : (
                        <>
                          <img
                            src={img.image_url}
                            alt={img.context}
                            loading="lazy"
                            className="w-full h-full object-cover"
                            onError={() => handleImgError(img.image_url)}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <Eye size={20} className="text-white drop-shadow-lg" />
                          </div>
                        </>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-[11px] font-bold text-gray-800 leading-tight truncate" title={img.context}>
                        {img.context || 'Unnamed'}
                      </div>
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {sortGrades(img.grade_levels).map(g => (
                          <span key={g} className="text-[9px] px-1.5 py-0.5 bg-[#002FA7]/10 text-[#002FA7] font-semibold">
                            {g}
                          </span>
                        ))}
                        <span className="text-[9px] text-gray-400 ml-auto">{img.question_count}q</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Lightbox Preview */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="image-preview-modal">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{preview.context}</h3>
                <div className="flex items-center gap-2 mt-1">
                  {sortGrades(preview.grade_levels).map(g => (
                    <span key={g} className="text-xs px-2 py-0.5 bg-[#002FA7]/10 text-[#002FA7] font-semibold">{g}</span>
                  ))}
                  <span className="text-xs text-gray-500">{preview.question_count} question(s)</span>
                </div>
              </div>
              <button onClick={() => setPreview(null)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 transition-colors" data-testid="close-preview-btn">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <img src={preview.image_url} alt={preview.context} className="w-full max-h-[60vh] object-contain bg-gray-50" />
            </div>
            {preview.sample_stimulus && (
              <div className="px-4 pb-4">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Sample Stimulus</div>
                <div className="text-sm text-gray-700 bg-gray-50 p-3 border border-gray-200 italic">
                  "{preview.sample_stimulus}"
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Rule1bParityCard = () => {
  // v6.32.176 — Rule 1b Print Parity stats card. Surfaces compliance %,
  // per-rule breakdown, and any stale grade+modality pairs that need a
  // press regen. Pure read-only — calls /api/audit/rule-1b-parity.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/audit/rule-1b-parity`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const compliance = data?.compliance_pct ?? null;
  const tone = compliance == null
    ? 'border-gray-200'
    : compliance >= 98 ? 'border-emerald-300 bg-emerald-50'
    : compliance >= 90 ? 'border-amber-300 bg-amber-50'
    : 'border-red-300 bg-red-50';
  const numTone = compliance == null
    ? 'text-gray-400'
    : compliance >= 98 ? 'text-emerald-700'
    : compliance >= 90 ? 'text-amber-700'
    : 'text-red-700';

  return (
    <div className={`border-2 ${tone} p-5 mt-6`} data-testid="rule-1b-parity-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-bold text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <ShieldCheck size={20} className="text-[#002FA7]" />
            Rule 1b Print Parity
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Per-row WIDA item-type fidelity check for the print Teacher Manual. Run any time at $0 content-engine cost.
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          className="h-9 px-3"
          data-testid="rule-1b-parity-refresh"
        >
          {loading
            ? <Loader2 size={14} className="mr-1.5 animate-spin" />
            : <RefreshCw size={14} className="mr-1.5" />}
          Refresh
        </Button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-200 p-3 text-sm text-red-700" data-testid="rule-1b-parity-error">
          {error}
        </div>
      )}

      {data && !error && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="border-2 border-gray-200 bg-white p-3 text-center">
              <div className={`text-2xl font-black ${numTone}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {compliance != null ? `${compliance}%` : '—'}
              </div>
              <div className="text-xs text-gray-500 font-semibold">Compliance</div>
            </div>
            <div className="border-2 border-gray-200 bg-white p-3 text-center">
              <div className="text-2xl font-black text-[#002FA7]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {data.rows_audited.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 font-semibold">Rows Audited</div>
            </div>
            <div className={`border-2 p-3 text-center ${data.row_violations > 0 ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
              <div className={`text-2xl font-black ${data.row_violations > 0 ? 'text-red-600' : 'text-emerald-700'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {data.row_violations}
              </div>
              <div className="text-xs text-gray-500 font-semibold">Row Violations</div>
            </div>
            <div className={`border-2 p-3 text-center ${data.stale_prints > 0 ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
              <div className={`text-2xl font-black ${data.stale_prints > 0 ? 'text-amber-700' : 'text-emerald-700'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {data.stale_prints}
              </div>
              <div className="text-xs text-gray-500 font-semibold">Stale Prints</div>
            </div>
          </div>

          {Object.keys(data.by_rule || {}).length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Violations by Rule</div>
              <div className="space-y-1.5">
                {Object.entries(data.by_rule)
                  .sort((a, b) => b[1] - a[1])
                  .map(([rule, count]) => (
                    <div key={rule} className="flex items-center justify-between bg-white border border-gray-200 px-3 py-2 text-sm" data-testid={`rule-1b-row-${rule}`}>
                      <span className="font-mono text-xs text-gray-700">{rule}</span>
                      <span className="font-bold text-gray-900">{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {(data.stale_pairs || []).length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-amber-700 uppercase mb-2">Stale Print Masters — Regenerate</div>
              <div className="space-y-1.5">
                {data.stale_pairs.map((sp, i) => (
                  <div key={i} className="flex items-center justify-between bg-amber-50 border border-amber-200 px-3 py-2 text-sm">
                    <span className="font-mono text-xs">grade={sp.grade}  modality={sp.modality}</span>
                    <span className="font-bold text-amber-800">{sp.count} row{sp.count === 1 ? '' : 's'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.row_violations === 0 && data.stale_prints === 0 && (
            <div className="mt-4 bg-emerald-100 border border-emerald-200 p-3 text-sm text-emerald-800 flex items-center gap-2">
              <CheckCircle size={16} className="text-emerald-600" />
              Rule 1b print parity: clean. Every row is structurally ready for print.
            </div>
          )}

          <div className="mt-3 text-xs text-gray-400">
            Last audit: {new Date(data.ts).toLocaleString()}
          </div>
        </>
      )}
    </div>
  );
};


const LaunchReadinessTab = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    fetchLatest();
  }, []);

  const fetchLatest = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audit/launch-readiness/latest`);
      if (res.data && res.data.sections) {
        setReport(res.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const runAudit = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/audit/launch-readiness`);
      setReport(res.data);
    } catch (err) {
      console.error("Audit failed:", err);
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="launch-readiness-tab">
      {/* Header — v6.32.51: sticky so the [Run Full Audit] button stays
          visible while the operator scrolls through long failure detail.
          Top offset 0 sits below the global app header which uses its
          own sticky positioning above this content area. */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 -mx-4 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Rocket size={22} className="text-[#002FA7]" />
            Launch Readiness Audit
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Comprehensive checks across content, images, APIs, auth, routes, and external links
          </p>
        </div>
        <Button
          onClick={runAudit}
          disabled={running}
          className="btn-sharp-primary h-11 px-6"
          data-testid="run-audit-btn"
        >
          {running ? (
            <><Loader2 size={16} className="mr-2 animate-spin" /> Running Audit...</>
          ) : (
            <><RefreshCw size={16} className="mr-2" /> Run Full Audit</>
          )}
        </Button>
      </div>

      {!report ? (
        <div className="border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <ShieldCheck size={48} className="text-gray-300 mx-auto mb-4" />
          <h3 className="font-bold text-lg text-gray-600 mb-2">No Audit Report Yet</h3>
          <p className="text-sm text-gray-500 mb-6">
            Click "Run Full Audit" to scan the entire platform for launch readiness.
          </p>
          <Button onClick={runAudit} disabled={running} className="btn-sharp-primary">
            {running ? <><Loader2 size={16} className="mr-2 animate-spin" /> Running...</> : "Run First Audit"}
          </Button>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="border-2 border-gray-200 p-4 text-center">
              <div className="text-2xl font-black text-[#002FA7]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {report.summary.total_checks}
              </div>
              <div className="text-xs text-gray-500 font-semibold">Total Checks</div>
            </div>
            <div className="border-2 border-emerald-200 bg-emerald-50 p-4 text-center">
              <div className="text-2xl font-black text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {report.summary.passed}
              </div>
              <div className="text-xs text-emerald-600 font-semibold">Passed</div>
            </div>
            <div className={`border-2 p-4 text-center ${report.summary.warnings > 0 ? 'border-amber-200 bg-amber-50' : 'border-gray-200'}`}>
              <div className={`text-2xl font-black ${report.summary.warnings > 0 ? 'text-amber-600' : 'text-gray-400'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {report.summary.warnings}
              </div>
              <div className="text-xs text-gray-500 font-semibold">Warnings</div>
            </div>
            <div className={`border-2 p-4 text-center ${report.summary.failures > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
              <div className={`text-2xl font-black ${report.summary.failures > 0 ? 'text-red-600' : 'text-gray-400'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {report.summary.failures}
              </div>
              <div className="text-xs text-gray-500 font-semibold">Failures</div>
            </div>
            <div className={`border-2 p-4 text-center ${report.summary.launch_ready ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
              <div className={`text-2xl font-black ${report.summary.launch_ready ? 'text-emerald-700' : 'text-red-600'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {report.summary.pass_rate}%
              </div>
              <div className={`text-xs font-semibold ${report.summary.launch_ready ? 'text-emerald-600' : 'text-red-600'}`}>
                {report.summary.launch_ready ? 'LAUNCH READY' : 'NOT READY'}
              </div>
            </div>
          </div>

          {/* Launch Status Banner */}
          {report.summary.launch_ready ? (
            <div className="border-2 border-emerald-300 bg-emerald-50 p-5 flex items-center gap-4" data-testid="audit-launch-ready">
              <div className="w-12 h-12 bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                <ShieldCheck size={28} />
              </div>
              <div>
                <div className="font-bold text-emerald-800 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Platform is Launch Ready
                </div>
                <div className="text-sm text-emerald-700">
                  All {report.summary.total_checks} automated checks passed. {report.summary.warnings > 0 ? `${report.summary.warnings} non-blocking warnings to review.` : 'No warnings.'}
                  {' '}Audit run: {new Date(report.timestamp).toLocaleString()}
                </div>
              </div>
            </div>
          ) : (
            <div className="border-2 border-red-300 bg-red-50 p-5 flex items-center gap-4" data-testid="audit-not-ready">
              <div className="w-12 h-12 bg-red-600 text-white flex items-center justify-center flex-shrink-0">
                <XCircle size={28} />
              </div>
              <div>
                <div className="font-bold text-red-800 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {report.summary.failures} Issue(s) Need Attention
                </div>
                <div className="text-sm text-red-700">
                  Resolve failures before launch. Expand each section below for details. Audit run: {new Date(report.timestamp).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {/* Meta */}
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><Clock size={12} /> {new Date(report.timestamp).toLocaleString()}</span>
            <span>{report.version}</span>
          </div>

          {/* Section Details */}
          <div className="space-y-3">
            {Object.entries(report.sections).map(([key, section]) => (
              <SectionCard key={key} sectionKey={key} section={section} onRerun={runAudit} running={running} />
            ))}
          </div>

          {/* Big Picture Image Gallery */}
          <ImageGallery />

          {/* v6.32.176 — Rule 1b Print Parity card */}
          <Rule1bParityCard />

          {/* Human Review Checklist */}
          <div className="border-2 border-blue-200 bg-blue-50 p-6 mt-6">
            <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <AlertTriangle size={18} className="text-blue-600" />
              Human Review Checklist
            </h3>
            <p className="text-sm text-blue-800 mb-4">
              These items cannot be verified automatically and require human review before launch:
            </p>
            <div className="space-y-2">
              {[
                "Visual correctness — Do Big Picture images match the cultural scenes described in audio/captions?",
                "Audio-caption alignment — Does the TTS audio accurately describe what's shown in each image?",
                "Print layout quality — Print a sample assessment from each grade band and verify margins, fonts, and readability",
                "Pedagogical accuracy — Have curriculum experts reviewed question difficulty and WIDA level alignment?",
                "Accessibility — Test with screen readers, keyboard-only navigation, and high contrast mode",
                "Mobile responsiveness — Test the full exam flow on tablets (primary student device in schools)",
                "Browser compatibility — Test on Chrome, Edge, Safari, and Firefox",
                "Network resilience — Test with slow/interrupted connections (school WiFi can be unreliable)",
              ].map((item, i) => (
                <label key={i} className="flex items-start gap-3 text-sm text-blue-800 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 accent-blue-600" />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LaunchReadinessTab;
